import { CHANNEL_ICON, CHANNEL_LABEL } from '../notifications/notification-format';

/**
 * A set of channels, shown the way the inbox shows them.
 *
 * The icons and labels come from `notification-format` — the same map the inbox
 * list and the notification detail page read — so a channel added there appears
 * everywhere at once rather than in whichever screens someone remembered.
 *
 * Two densities, matching the two the inbox already uses:
 *
 *   `icons`  bare icons, for a table cell where a column of words would not fit
 *   `chips`  icon plus label, for a detail page where there is room to say it
 *
 * An unknown channel renders as a neutral circle under its own code rather than
 * disappearing: a channel the UI does not recognise is still one the engine will
 * send on, and hiding it would make the list quietly disagree with the config.
 */
interface ChannelIconsProps {
  channels?: string[] | null;
  variant?: 'icons' | 'chips';
  /** Shown when there are none — a dash in a table, a sentence in a panel. */
  empty?: string;
  testId?: string;
}

const ChannelIcons = ({
  channels, variant = 'icons', empty = '-', testId,
}: ChannelIconsProps) => {
  const list = (channels || []).filter(Boolean);

  if (!list.length) {
    return <span className="text-xs text-gray-400" data-testid={testId}>{empty}</span>;
  }

  if (variant === 'chips') {
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={testId}>
        {list.map((c) => (
          <span
            key={c}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200"
          >
            <i className={`pi ${CHANNEL_ICON[c] || 'pi-circle'} text-[11px] text-gray-400`} />
            {CHANNEL_LABEL[c] || c}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      {list.map((c) => (
        // The label is the title, not text beside it: in a table cell the icons
        // have to stay one line, and the name is one hover away.
        <i
          key={c}
          className={`pi ${CHANNEL_ICON[c] || 'pi-circle'} text-sm text-ink-muted`}
          title={CHANNEL_LABEL[c] || c}
          aria-label={CHANNEL_LABEL[c] || c}
        />
      ))}
    </div>
  );
};

export default ChannelIcons;
